import { useCallback, useEffect, useState } from 'react';

import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import {
  fetchAdminPaiementsImpayes,
  fetchAdminPaiementsProjections,
  runAdminPaymentDueAlerts,
  runAdminPaymentDueAlertForInscription,
  validateAdminVirement,
} from '../../../services/academiaAdminService';
import { formatMoney } from '../../../utils/currency';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';

export default function AcademiaPaiementsPanel() {
  const [impayes, setImpayes] = useState<Record<string, unknown>[]>([]);
  const [projections, setProjections] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [relanceLoadingId, setRelanceLoadingId] = useState<string | null>(null);

  const { paginatedItems, currentPage, totalPages, onPageChange, summary } = useAdminTablePagination(impayes);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [imp, proj] = await Promise.all([fetchAdminPaiementsImpayes(), fetchAdminPaiementsProjections()]);
      setImpayes(imp);
      setProjections(proj);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useRegisterPortalRefresh(load);
  useEffect(() => {
    void load();
  }, [load]);

  const handleValidate = async (txId: string) => {
    try {
      await validateAdminVirement(txId);
      setToast('Paiement validé.');
      await load();
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const handleAlerts = async () => {
    try {
      const r = await runAdminPaymentDueAlerts();
      const sent = r?.sent ?? 0;
      const emails = r?.emailsSent ?? 0;
      setToast(
        sent > 0
          ? `${sent} relance(s) envoyée(s) (notification${emails > 0 ? `, ${emails} e-mail(s)` : ''}).`
          : 'Aucune relance envoyée (délai minimum non atteint ou dossiers déjà soldés).',
      );
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const handleRelancerInscription = async (inscriptionId: string) => {
    setRelanceLoadingId(inscriptionId);
    try {
      const r = await runAdminPaymentDueAlertForInscription(inscriptionId);
      setToast(r?.message ?? 'Relance envoyée à l’apprenant (notification et e-mail).');
      await load();
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Erreur lors de la relance');
    } finally {
      setRelanceLoadingId(null);
    }
  };

  const proj = projections as {
    recettesJournalieres?: number;
    recettesMensuelles?: number;
    bonusDistribuesMois?: number;
    montantImpayeTotal?: number;
    paiementsEnAttente?: number;
    projections?: { projectionFinDeMois?: number; projectionTrimestre?: number; hypothese?: string };
  } | null;

  return (
    <AdminPageShell
      title="Gestion des paiements"
      description="Impayés, encaissements, bonus parrainage et projections."
      sideCard={{
        label: 'Impayés',
        value: String(impayes.length),
        helper: proj ? formatMoney(proj.montantImpayeTotal ?? 0, 'CDF') + ' à recouvrer' : '',
      }}
      isLoading={loading}
      isError={Boolean(error)}
      onRetry={load}
      loadingMessage="Chargement…"
      errorMessage={error ?? undefined}
    >
      {toast ? (
        <p className="mb-4 rounded-lg bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">{toast}</p>
      ) : null}

      {proj ? (
        <section className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-on-surface-variant">Recettes du jour</p>
            <p className="mt-1 text-xl font-black text-primary">{formatMoney(proj.recettesJournalieres ?? 0, 'CDF')}</p>
          </div>
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-on-surface-variant">Recettes du mois</p>
            <p className="mt-1 text-xl font-black text-primary">{formatMoney(proj.recettesMensuelles ?? 0, 'CDF')}</p>
          </div>
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-on-surface-variant">Bonus parrainage (mois)</p>
            <p className="mt-1 text-xl font-black text-slate-900">{formatMoney(proj.bonusDistribuesMois ?? 0, 'CDF')}</p>
          </div>
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-on-surface-variant">Projection fin de mois</p>
            <p className="mt-1 text-xl font-black text-emerald-700">
              {formatMoney(proj.projections?.projectionFinDeMois ?? 0, 'CDF')}
            </p>
            <p className="mt-1 text-[10px] text-on-surface-variant">{proj.projections?.hypothese}</p>
          </div>
        </section>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void handleAlerts()}
          className="inline-flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-bold text-primary"
        >
          <LucideSymbolIcon name="notifications" tone="inherit" size={18} />
          Relancer alertes impayés
        </button>
      </div>

      <section className="rounded-2xl border border-outline-variant/15 bg-white shadow-sm">
        <h2 className="border-b border-outline-variant/10 px-5 py-4 text-lg font-black">Suivi des impayés</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-surface-container-low text-xs uppercase text-on-surface-variant">
              <tr>
                <th className="px-4 py-3">Apprenant</th>
                <th className="px-4 py-3">Matricule</th>
                <th className="px-4 py-3">Reste</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-on-surface-variant">
                    Aucun impayé.
                  </td>
                </tr>
              ) : (
                paginatedItems.map((row) => (
                  <tr key={String(row.id)} className="border-t border-outline-variant/10">
                    <td className="px-4 py-3">
                      <p className="font-medium">{String((row.apprenant as { nom?: string })?.nom ?? '—')}</p>
                      <p className="text-xs text-on-surface-variant">
                        {(row.apprenant as { telephone?: string })?.telephone}
                      </p>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{String(row.matricule)}</td>
                    <td className="px-4 py-3 font-bold text-amber-800">
                      {formatMoney(Number(row.resteAPayer ?? 0), 'CDF')}
                    </td>
                    <td className="px-4 py-3 text-xs">{String(row.statut)}</td>
                    <td className="px-4 py-3 text-right">
                      {(((row.transactionsEnAttente as { id: string; modePaiement: string; reference?: string }[]) ??
                        []) as { id: string; modePaiement: string; reference?: string }[]).length > 0 ? (
                        (row.transactionsEnAttente as { id: string; modePaiement: string; reference?: string }[]).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="mb-1 block w-full rounded-lg bg-primary/10 px-2 py-1 text-xs font-bold text-primary"
                            onClick={() => void handleValidate(t.id)}
                          >
                            Valider {t.modePaiement === 'VIREMENT_BANCAIRE' ? 'virement' : 'carte'}{' '}
                            {t.reference ? `(${t.reference})` : ''}
                          </button>
                        ))
                      ) : (
                        <button
                          type="button"
                          className="block w-full rounded-lg border border-primary/30 bg-primary/5 px-2 py-1 text-xs font-bold text-primary disabled:opacity-60"
                          disabled={relanceLoadingId === String(row.id)}
                          onClick={() => void handleRelancerInscription(String(row.id))}
                        >
                          {relanceLoadingId === String(row.id) ? 'Envoi…' : 'Relancer'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {impayes.length > 0 ? (
          <AdminPagination
            className="border-t border-outline-variant/10 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={summary}
          />
        ) : null}
      </section>
    </AdminPageShell>
  );
}
